/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useMemo } from 'react';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import type { DataTransferSummary, DataTransferSurveyResult } from '@g17eco/types/data-transfer';
import { roundTo } from '@utils/number';

const pct = (value: number, total: number): number => {
  if (total === 0) {
    return 0;
  }
  return roundTo((value / total) * 100, 0);
};

const SummaryCard = ({ value, label, tooltip, total, colorClass }: {
  value: number;
  label: string;
  tooltip: string;
  total: number;
  colorClass?: string;
}) => (
  <div className='col'>
    <SimpleTooltip text={tooltip}>
      <div className='card text-center p-2' style={{ cursor: 'help' }}>
        <div className={`h5 mb-0 ${colorClass ?? ''}`}>{value}</div>
        <small>{label} <span className='text-ThemeTextLight'>({pct(value, total)}%)</span></small>
      </div>
    </SimpleTooltip>
  </div>
);

const aggregateSummaries = (surveys: DataTransferSurveyResult[]): DataTransferSummary => {
  return surveys.reduce<DataTransferSummary>(
    (acc, s) => ({
      total: acc.total + s.summary.total,
      comparable: acc.comparable + s.summary.comparable,
      matching: acc.matching + s.summary.matching,
      differing: acc.differing + s.summary.differing,
      pending: acc.pending + s.summary.pending,
      noData: acc.noData + s.summary.noData,
    }),
    { total: 0, comparable: 0, matching: 0, differing: 0, pending: 0, noData: 0 },
  );
};

export const DataTransferSummaryCards = ({ surveys }: { surveys: DataTransferSurveyResult[] }) => {
  const summary = useMemo(() => aggregateSummaries(surveys), [surveys]);
  return (
    <div className='row mb-3'>
      <div className='col'>
        <div className='card text-center p-2'>
          <div className='h5 mb-0'>{summary.total}</div>
          <small>Total <span className='text-ThemeTextLight'>({surveys.length} survey{surveys.length !== 1 ? 's' : ''})</span></small>
        </div>
      </div>
      <div className='col'>
        <SimpleTooltip text={`${summary.comparable} rows where both survey and integration values exist.`}>
          <div className='card text-center p-2' style={{ cursor: 'help' }}>
            <div className='h5 mb-0'>
              <span className='text-ThemeSuccessMedium'>{summary.matching}</span>
              <span className='text-ThemeTextLight mx-1'>/</span>
              <span>{summary.comparable}</span>
            </div>
            <small>
              Matched
              {summary.differing > 0 && (
                <span className='text-ThemeDangerMedium ms-1'>({summary.differing} differing)</span>
              )}
            </small>
          </div>
        </SimpleTooltip>
      </div>
      <SummaryCard
        value={summary.pending} label='Pending' total={summary.total}
        tooltip='Identified but no integration data flowing yet.'
      />
      <SummaryCard
        value={summary.noData} label='No Data' total={summary.total}
        colorClass='text-ThemeWarningMedium'
        tooltip='Data not available from the integration provider.'
      />
    </div>
  );
};
